import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { Settings as SettingsIcon, X as XIcon, Zap as ZapIcon } from 'lucide-react';

import { useUserStateContext } from '@/shared/components/UserStateProvider';
import { conditionsForEventType, teamConditionSupported } from '@c360/shared';
import { useClipRules, type ClipRule, type ClipRuleInput, type LeagueClipEvent } from '../hooks/useClipRules';

/**
 * Team is stored as the `team` condition rather than a column: the scheduler
 * reads the side straight off the tracked participant (`"home"` / `"away"`),
 * so it is a predicate like any other. An empty value means no filter.
 */
const TEAM_SIDES = [
  { value: '', label: 'All teams' },
  { value: 'home', label: 'Home team' },
  { value: 'away', label: 'Away team' },
];

const teamLabel = (value?: unknown) => TEAM_SIDES.find((t) => t.value === (value ?? ''))?.label ?? String(value);

const emptyDraft: ClipRuleInput = {
  name: '',
  description: '',
  enabled: false,
  eventType: 'goal',
  conditions: {},
  preRollMs: 10000,
  postRollMs: 10000,
  trackTarget: 'scorer',
};

const msToSeconds = (ms?: number) => (ms == null ? '' : String(ms / 1000));
const secondsToMs = (s: string) => Math.round(parseFloat(s || '0') * 1000);

/**
 * Schedule is stored as two absolute bounds rather than a mode, so the mode is
 * derived: no bounds is the full season, a single local day is "specific day",
 * anything else is a window.
 */
type ScheduleMode = 'always' | 'day' | 'window';

const SCHEDULE_LABELS: Record<ScheduleMode, string> = {
  always: 'Full season',
  day: 'Specific day',
  window: 'Time window',
};

const pad = (n: number) => String(n).padStart(2, '0');

/** ISO instant -> the YYYY-MM-DD a <input type="date"> expects, in local time. */
const toDateInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** YYYY-MM-DD -> the instant that local day starts / ends. */
const startOfLocalDay = (value: string): string | null => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
};
const endOfLocalDay = (value: string): string | null => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
};

const scheduleModeOf = (rule: Pick<ClipRule, 'activeFrom' | 'activeTo'>): ScheduleMode => {
  if (!rule.activeFrom && !rule.activeTo) return 'always';
  if (rule.activeFrom && rule.activeTo && toDateInput(rule.activeFrom) === toDateInput(rule.activeTo)) {
    return 'day';
  }
  return 'window';
};

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';

/** Schedule as a short phrase for the rule card's chip. */
const describeSchedule = (rule: Pick<ClipRule, 'activeFrom' | 'activeTo'>): string => {
  switch (scheduleModeOf(rule)) {
    case 'always':
      return 'Full season';
    case 'day':
      return formatDate(rule.activeFrom);
    default:
      if (rule.activeFrom && rule.activeTo) return `${formatDate(rule.activeFrom)} – ${formatDate(rule.activeTo)}`;
      return rule.activeFrom ? `From ${formatDate(rule.activeFrom)}` : `Until ${formatDate(rule.activeTo)}`;
  }
};

/** True while now() is inside the rule's window — mirrors the scheduler's cache filter. */
const isWithinSchedule = (rule: Pick<ClipRule, 'activeFrom' | 'activeTo'>): boolean => {
  const now = Date.now();
  if (rule.activeFrom && new Date(rule.activeFrom).getTime() > now) return false;
  if (rule.activeTo && new Date(rule.activeTo).getTime() < now) return false;
  return true;
};

/**
 * Render the conditions json as something an operator can read at a glance.
 * `team` is excluded: it has its own row on the card and its own control in the
 * panel, so repeating it here would read as two separate filters.
 */
const describeConditions = (conditions: Record<string, unknown>): string => {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(conditions ?? {})) {
    switch (key) {
      case 'team':
        break;
      case 'requireSuccess':
        if (value !== false) parts.push('successful only');
        break;
      case 'clockLessThan':
        parts.push(`clock < ${value}s`);
        break;
      case 'clockAtLeast':
        parts.push(`clock ≥ ${value}s`);
        break;
      case 'periodIn':
        parts.push(`period ${Array.isArray(value) ? value.join(', ') : value}`);
        break;
      case 'basketKind':
        parts.push(String(value).toLowerCase().replace('_', '-'));
        break;
      default:
        parts.push(`${key}: ${JSON.stringify(value)}`);
    }
  }
  return parts.join(' · ');
};

/** Conditions minus `team`, which the Team control owns. */
const conditionsWithoutTeam = (conditions?: Record<string, unknown> | null) => {
  const { team: _team, ...rest } = conditions ?? {};
  return rest;
};

/** Uppercase field label, per the mock. */
const FieldLabel: React.FC<{ children: React.ReactNode; hint?: string }> = ({ children, hint }) => (
  <Box sx={{ mb: hint ? 1 : 1.5 }}>
    <Typography sx={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {children}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Box>
);

/** One "Label: value" line in a rule card. */
const CardRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
    <Box component="span" sx={{ opacity: 0.65 }}>
      {label}:
    </Box>{' '}
    {children}
  </Typography>
);

const ClipRulesConfig: React.FC = () => {
  const { clipRules, leagueClipEventTypes, isLoading, createRule, updateRule, deleteRule } = useClipRules();
  const { availableLeagues } = useUserStateContext();

  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState<ClipRule | null>(null);
  const [draft, setDraft] = useState<ClipRuleInput>(emptyDraft);
  const [teamSide, setTeamSide] = useState('');
  const [conditionsText, setConditionsText] = useState('{}');
  const [conditionsOpen, setConditionsOpen] = useState(false);
  const [conditionsError, setConditionsError] = useState<string | null>(null);
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('always');
  const [scheduleStart, setScheduleStart] = useState('');
  const [scheduleEnd, setScheduleEnd] = useState('');

  // Only the current org's leagues (server-resolved from org access), not the
  // rules table — deriving it from the rules was circular, so with none yet
  // "New rule" could never be enabled.
  const leagues = useMemo(
    () =>
      availableLeagues.map((league) => ({
        id: league.id,
        name: league.displayName || league.name || `League ${league.id}`,
      })),
    [availableLeagues],
  );

  // Events, tracked objects and their labels all come per league
  // (`league.clipEventTypes`); a stored value the league no longer offers
  // falls back to its raw key.
  const eventsFor = (leagueId?: number): LeagueClipEvent[] =>
    leagueClipEventTypes.find((l) => l.id === leagueId)?.clipEventTypes ?? [];
  const eventFor = (leagueId?: number, eventType?: string) => eventsFor(leagueId).find((e) => e.value === eventType);
  const eventLabel = (leagueId?: number, eventType?: string) =>
    eventFor(leagueId, eventType)?.label ?? eventType ?? '—';
  const trackTargetOptions = (leagueId?: number, eventType?: string) =>
    eventFor(leagueId, eventType)?.trackedObjects ?? [];
  const trackTargetLabel = (leagueId: number | undefined, eventType: string | undefined, trackTarget: string) =>
    trackTargetOptions(leagueId, eventType).find((t) => t.trackedObject === trackTarget)?.label ?? trackTarget;

  // Point the draft at a league, dropping an event / tracked object the league
  // doesn't offer rather than saving a rule that would never match.
  const withLeague = (current: ClipRuleInput, leagueId?: number): ClipRuleInput => {
    const events = eventsFor(leagueId);
    const eventType = events.some((e) => e.value === current.eventType) ? current.eventType : events[0]?.value;
    const targets = trackTargetOptions(leagueId, eventType);
    const trackTarget = targets.some((t) => t.trackedObject === current.trackTarget)
      ? current.trackTarget
      : targets[0]?.trackedObject;
    return { ...current, leagueId, eventType, trackTarget };
  };

  // An edited rule keeps its stored event and tracked object even if the
  // league has since stopped offering them — the API accepts unchanged values,
  // and a blank select would hide what the rule actually does.
  const eventOptions = (() => {
    const events = eventsFor(draft.leagueId);
    return editing && draft.eventType && !events.some((e) => e.value === draft.eventType)
      ? [...events, { value: draft.eventType, label: draft.eventType, trackedObjects: [] }]
      : events;
  })();
  const trackedObjectOptions = (() => {
    const options = trackTargetOptions(draft.leagueId, draft.eventType);
    return editing && draft.trackTarget && !options.some((t) => t.trackedObject === draft.trackTarget)
      ? [...options, { trackedObject: draft.trackTarget, label: draft.trackTarget }]
      : options;
  })();

  const openCreate = () => {
    setEditing(null);
    setDraft(withLeague(emptyDraft, leagues[0]?.id));
    setTeamSide('');
    setConditionsText('{}');
    setConditionsOpen(false);
    setConditionsError(null);
    setScheduleMode('always');
    setScheduleStart('');
    setScheduleEnd('');
    setPanelOpen(true);
  };

  const openEdit = (rule: ClipRule) => {
    const rest = conditionsWithoutTeam(rule.conditions);
    setEditing(rule);
    setDraft({
      name: rule.name,
      description: rule.description ?? '',
      eventType: rule.eventType,
      leagueId: rule.leagueId,
      preRollMs: rule.preRollMs,
      postRollMs: rule.postRollMs,
      trackTarget: rule.trackTarget,
    });
    setTeamSide(String(rule.conditions?.team ?? ''));
    setConditionsText(JSON.stringify(rest, null, 2));
    setConditionsOpen(Object.keys(rest).length > 0);
    setConditionsError(null);
    setScheduleMode(scheduleModeOf(rule));
    setScheduleStart(toDateInput(rule.activeFrom));
    setScheduleEnd(toDateInput(rule.activeTo));
    setPanelOpen(true);
  };

  const handleSave = () => {
    let conditions: Record<string, unknown>;
    try {
      conditions = conditionsWithoutTeam(JSON.parse(conditionsText || '{}'));
    } catch {
      setConditionsError('Not valid JSON');
      setConditionsOpen(true);
      return;
    }
    // Only persist the side while its control is visible: a leftover selection
    // on an event/target combination with no side signal would make the rule
    // silently never match (the evaluator fails closed on a missing side).
    if (teamSide && teamFilterAvailable) conditions.team = teamSide;

    // Bounds are absolute instants; a "specific day" is just the local day's
    // start and end, which is why it needs no separate representation.
    const schedule =
      scheduleMode === 'always'
        ? { activeFrom: null, activeTo: null }
        : scheduleMode === 'day'
          ? { activeFrom: startOfLocalDay(scheduleStart), activeTo: endOfLocalDay(scheduleStart) }
          : { activeFrom: startOfLocalDay(scheduleStart), activeTo: endOfLocalDay(scheduleEnd) };

    const payload = { ...draft, conditions, ...schedule };
    if (editing) {
      updateRule.mutate({ id: editing.id, ...payload }, { onSuccess: () => setPanelOpen(false) });
    } else {
      createRule.mutate(payload, { onSuccess: () => setPanelOpen(false) });
    }
  };

  // The puck has no team of its own, so a side filter can only be inferred from
  // the event — which carries one for goals and shootout goals, nothing else.
  // Same predicate the business-api validator applies, from @c360/shared.
  const teamFilterAvailable = teamConditionSupported(draft.eventType ?? '', draft.trackTarget ?? '');

  const panel = (
    <Dialog open={panelOpen} onClose={() => setPanelOpen(false)} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>{editing ? 'Edit rule' : 'Create rule'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <FieldLabel>Rule name</FieldLabel>
            <TextField
              value={draft.name ?? ''}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Goal by top scorer"
              size="small"
              fullWidth
            />
            <TextField
              value={draft.description ?? ''}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Description (optional)"
              size="small"
              fullWidth
              sx={{ mt: 1.5 }}
            />
          </Box>

          {/* Single-league orgs have nothing to choose: openCreate already
              set draft.leagueId to that league. */}
          {leagues.length > 1 && (
            <Box>
              <FieldLabel hint="Rules are scoped to a league — the events of interest differ per sport">
                League
              </FieldLabel>
              <TextField
                select
                value={draft.leagueId ?? ''}
                onChange={(e) => setDraft(withLeague(draft, Number(e.target.value)))}
                disabled={!!editing}
                helperText={editing ? 'League cannot be changed after creation' : undefined}
                size="small"
                fullWidth
              >
                {leagues.map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          <Box>
            <FieldLabel>Event of interest</FieldLabel>
            <TextField
              select
              value={draft.eventType ?? ''}
              onChange={(e) => {
                // Targets are event-specific, so drop one the new event can't
                // carry rather than saving a rule that would never match.
                const eventType = e.target.value;
                const valid = trackTargetOptions(draft.leagueId, eventType);
                const trackTarget = valid.some((t) => t.trackedObject === draft.trackTarget)
                  ? draft.trackTarget
                  : valid[0]?.trackedObject;
                setDraft({ ...draft, eventType, trackTarget });
              }}
              size="small"
              fullWidth
            >
              {eventOptions.map((e) => (
                <MenuItem key={e.value} value={e.value}>
                  {e.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <FieldLabel hint="What Auto-Iso follows for the length of the clip">Tracked object</FieldLabel>
            <TextField
              select
              value={draft.trackTarget ?? 'scorer'}
              onChange={(e) => setDraft({ ...draft, trackTarget: e.target.value })}
              size="small"
              fullWidth
            >
              {trackedObjectOptions.map((t) => (
                <MenuItem key={t.trackedObject} value={t.trackedObject}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <FieldLabel hint="Optional — defaults to 10s / 10s">Clip buffer</FieldLabel>
            <Stack direction="row" spacing={1.5}>
              <TextField
                label="Before (seconds)"
                type="number"
                value={msToSeconds(draft.preRollMs)}
                onChange={(e) => setDraft({ ...draft, preRollMs: secondsToMs(e.target.value) })}
                size="small"
                fullWidth
              />
              <TextField
                label="After (seconds)"
                type="number"
                value={msToSeconds(draft.postRollMs)}
                onChange={(e) => setDraft({ ...draft, postRollMs: secondsToMs(e.target.value) })}
                size="small"
                fullWidth
              />
            </Stack>
          </Box>

          {teamFilterAvailable && (
            <Box>
              <FieldLabel hint="Optional — defaults to all teams">Team</FieldLabel>
              <TextField select value={teamSide} onChange={(e) => setTeamSide(e.target.value)} size="small" fullWidth>
                {TEAM_SIDES.map((t) => (
                  <MenuItem key={t.value || 'all'} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          <Box>
            <FieldLabel hint="Outside its schedule a rule creates nothing, even while enabled">Schedule</FieldLabel>
            <TextField
              select
              value={scheduleMode}
              onChange={(e) => setScheduleMode(e.target.value as ScheduleMode)}
              size="small"
              fullWidth
            >
              {(Object.keys(SCHEDULE_LABELS) as ScheduleMode[]).map((mode) => (
                <MenuItem key={mode} value={mode}>
                  {SCHEDULE_LABELS[mode]}
                </MenuItem>
              ))}
            </TextField>

            {scheduleMode !== 'always' && (
              <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                <TextField
                  label={scheduleMode === 'day' ? 'Date' : 'Start date'}
                  type="date"
                  value={scheduleStart}
                  onChange={(e) => setScheduleStart(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  size="small"
                  fullWidth
                />
                {scheduleMode === 'window' && (
                  <TextField
                    label="End date"
                    type="date"
                    value={scheduleEnd}
                    onChange={(e) => setScheduleEnd(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    size="small"
                    fullWidth
                  />
                )}
              </Stack>
            )}
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch size="small" checked={conditionsOpen} onChange={(e) => setConditionsOpen(e.target.checked)} />
              }
              label={
                <Typography sx={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Advanced conditions
                </Typography>
              }
            />
            <Collapse in={conditionsOpen}>
              <TextField
                value={conditionsText}
                onChange={(e) => {
                  setConditionsText(e.target.value);
                  setConditionsError(null);
                }}
                error={!!conditionsError}
                helperText={
                  conditionsError ??
                  `e.g. {"clockLessThan": 5}. Supported for ${eventLabel(draft.leagueId, draft.eventType)}: ` +
                    conditionsForEventType(draft.eventType ?? '').join(', ')
                }
                multiline
                minRows={3}
                size="small"
                fullWidth
                sx={{ mt: 1 }}
              />
            </Collapse>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => setPanelOpen(false)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            !draft.name ||
            !draft.leagueId ||
            !draft.eventType ||
            (scheduleMode === 'day' && !scheduleStart) ||
            // A window may be open-ended on either side ("From …" / "Until …"
            // rules exist), so require at least one bound rather than both.
            (scheduleMode === 'window' && !scheduleStart && !scheduleEnd) ||
            createRule.isPending ||
            updateRule.isPending
          }
        >
          Save rule
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (clipRules.length === 0) {
    return (
      <>
        <Stack sx={{ alignItems: 'center', justifyContent: 'center', gap: 3, py: 10, textAlign: 'center' }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              color: 'primary.main',
            }}
          >
            <ZapIcon size={36} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 1.5 }}>No clip rules yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380, lineHeight: 1.6 }}>
              Set up a rule and CXVH will automatically generate clips whenever your events of interest occur.
            </Typography>
          </Box>
          <Button variant="contained" onClick={openCreate} disabled={!leagues.length}>
            Create automated clip rule
          </Button>
        </Stack>
        {panel}
      </>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Your clip rules</Typography>
          <Typography variant="caption" color="text.secondary">
            Enabled rules are evaluated against every live game in their league. Changes take effect within about 15
            seconds.
          </Typography>
        </Box>
        <Button variant="contained" onClick={openCreate} disabled={!leagues.length}>
          New rule
        </Button>
      </Stack>

      <Stack spacing={2}>
        {clipRules.map((rule) => {
          const isSystem = rule.isSystem;
          // Platform rules govern every org's games, so business-api rejects
          // any change to their shape — only enabling/pausing. Offering the
          // editor anyway meant every save failed, including setting a
          // schedule, and the toggle was the only control that worked.
          const isPlatform = rule.orgId === null;
          const conditionSummary = describeConditions(rule.conditions);
          return (
            <Paper
              key={rule.id}
              variant="outlined"
              sx={{ p: 2.5, opacity: rule.enabled ? 1 : 0.6, transition: 'opacity 0.2s' }}
            >
              <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 1 }}>{rule.name}</Typography>
                  <Stack spacing={0.75}>
                    <CardRow label="Event">{eventLabel(rule.leagueId, rule.eventType)}</CardRow>
                    <CardRow label="Tracked object">
                      {trackTargetLabel(rule.leagueId, rule.eventType, rule.trackTarget)}
                    </CardRow>
                    <CardRow label="Team">{teamLabel(rule.conditions?.team)}</CardRow>
                    <CardRow label="Clip buffer">
                      {rule.preRollMs / 1000}s before, {rule.postRollMs / 1000}s after
                    </CardRow>
                    {conditionSummary && <CardRow label="Conditions">{conditionSummary}</CardRow>}
                    {rule.description && (
                      <Typography variant="caption" color="text.secondary">
                        {rule.description}
                      </Typography>
                    )}
                  </Stack>
                </Box>

                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Tooltip title={rule.enabled ? 'Pause this rule' : 'Activate this rule'}>
                    <Switch
                      checked={rule.enabled}
                      onChange={(e) => updateRule.mutate({ id: rule.id, enabled: e.target.checked })}
                    />
                  </Tooltip>
                  <Tooltip
                    title={
                      isPlatform
                        ? 'Platform rules can only be activated or paused — create your own rule to change the shape'
                        : 'Edit rule'
                    }
                  >
                    <span>
                      <IconButton
                        disabled={isPlatform}
                        onClick={() => openEdit(rule)}
                        sx={{ width: 44, height: 44 }}
                        aria-label="Edit rule"
                      >
                        <SettingsIcon size={18} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={isSystem ? 'System rules cannot be deleted — pause it instead' : 'Delete rule'}>
                    <span>
                      <IconButton
                        color="error"
                        disabled={isSystem}
                        onClick={() => deleteRule.mutate(rule.id)}
                        sx={{ width: 44, height: 44 }}
                        aria-label="Delete rule"
                      >
                        <XIcon size={18} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Chip size="small" label={rule.league?.displayName || rule.league?.name || `League ${rule.leagueId}`} />
                <Chip size="small" color="primary" variant="outlined" label={describeSchedule(rule)} />
                {rule.enabled && !isWithinSchedule(rule) && (
                  <Chip size="small" color="warning" variant="outlined" label="Outside window" />
                )}
                {!rule.enabled && <Chip size="small" label="Paused" />}
                {isSystem ? (
                  <Chip size="small" variant="outlined" label="System" />
                ) : (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`By ${rule.creator?.firstName || rule.creator?.email || rule.createdBy || 'removed user'}`}
                  />
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {panel}
    </Box>
  );
};

export default ClipRulesConfig;
